import { type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import { TotpCodeField } from '@/components/TotpCodeField/TotpCodeField'
import styles from './CampaignTotpStepUpModal.module.css'

type CampaignTotpStepUpModalProps = {
  code: string
  enrollmentHint: string
  errorMessage: string | null
  isEnrollmentRequired: boolean
  isLoadingStatus: boolean
  isStatusUnavailable: boolean
  isVerifying: boolean
  onCancel: () => void
  onCodeChange: (value: string) => void
  onVerify: () => void
  subtitle: string
}

export function CampaignTotpStepUpModal({
  code,
  enrollmentHint,
  errorMessage,
  isEnrollmentRequired,
  isLoadingStatus,
  isStatusUnavailable,
  isVerifying,
  onCancel,
  onCodeChange,
  onVerify,
  subtitle,
}: CampaignTotpStepUpModalProps) {
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (isEnrollmentRequired || isLoadingStatus || isStatusUnavailable || isVerifying) return
    onVerify()
  }

  return createPortal(
    <div
      className={styles.overlay}
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          onCancel()
        }
      }}
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby="campaign-totp-step-up-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.header}>
          <div className={styles.headerIcon} aria-hidden>
            <svg viewBox="0 0 24 24" width="22" height="22" focusable="false">
              <path
                fill="currentColor"
                d="M12 1 3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11z"
              />
            </svg>
          </div>
          <div>
            <h2 id="campaign-totp-step-up-title" className={styles.title}>
              Confirme sua identidade
            </h2>
            <p className={styles.subtitle}>{subtitle}</p>
          </div>
        </div>

        {isLoadingStatus ? (
          <div className={styles.body}>
            <p className={styles.loadingText}>Verificando autenticação em duas etapas...</p>
          </div>
        ) : isEnrollmentRequired ? (
          <div className={styles.body}>
            <p className={styles.hint}>{enrollmentHint}</p>
            {errorMessage ? (
              <p className={styles.error} role="alert">
                {errorMessage}
              </p>
            ) : null}
            <div className={styles.footer}>
              <button type="button" className={styles.secondaryButton} onClick={onCancel}>
                Cancelar
              </button>
              <Link className={styles.primaryButton} to="/perfil/seguranca">
                Configurar 2FA
              </Link>
            </div>
          </div>
        ) : isStatusUnavailable ? (
          <div className={styles.body}>
            <p className={styles.hint}>
              Não foi possível confirmar a autenticação em duas etapas agora. Tente novamente em
              instantes.
            </p>
            {errorMessage ? (
              <p className={styles.error} role="alert">
                {errorMessage}
              </p>
            ) : null}
            <div className={styles.footer}>
              <button type="button" className={styles.secondaryButton} onClick={onCancel}>
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <form className={styles.body} onSubmit={handleSubmit}>
            <TotpCodeField
              id="campaign-totp-step-up-code"
              value={code}
              onChange={onCodeChange}
              disabled={isVerifying}
              autoFocus
            />
            {errorMessage ? (
              <p className={styles.error} role="alert">
                {errorMessage}
              </p>
            ) : null}
            <div className={styles.footer}>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={onCancel}
                disabled={isVerifying}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className={styles.primaryButton}
                disabled={isVerifying || code.length !== 6}
              >
                {isVerifying ? 'Confirmando...' : 'Confirmar código'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body
  )
}
